/**
 * Classe principale per le utility del tema Shopify nel progetto Climamarket.
 * Questa classe fornisce metodi statici per gestire carrello, cookie, ricerca,
 * raccomandazioni e altre funzionalità integrate con l'API di Shopify.
 * Gestisce anche eventi personalizzati e l'integrazione con Iubenda per la privacy.
 *
 * Documentazione: docs/js/res-shopify-theme.md
 */

if(!window.loadComponent) {
  let {loadComponent} = await import('../loader.js');
}
await loadComponent('./res/mc-utils.js', false);

class ShopifyTheme {
  // Questi sono gli eventi che vengono pubblicati da ShopifyTheme su window. Questi eventi sono accessibili sia dal tema sia dai WebPixel di Shopify dato che sono pubblicati con un metodo apposito.
  static EVENTS = {
    COOKIE_CONSENT: 'cookie:consent',       //Evento emesso quando l'utente accetta o non accetta i cookie
    COOKIE_CLOSED: 'cookie:closed',         //Evento emesso quando l'utente chiude il banner dei cookie
    CART_ADDED: 'cart:added',               //Evento emesso quando un prodotto viene aggiunto al carrello
    CART_UPDATED: 'cart:updated',           //Evento emesso quando il carrello viene aggiornato
    CART_REMOVED: 'cart:removed',           //Evento emesso quando un prodotto viene rimosso dal carrello
    SEARCH_PREDICTIVE: 'search:predictive', //Evento emesso quando viene effettuata una ricerca
    SEARCH: 'search:html',                  //Evento emesso quando viene effettuata una ricerca
  };

  // elenco di storage keys usati da ShopifyTheme
  static STORAGE = {
    COOKIE_CONSENT: 'cookie_consent',       // Key usata per salvare le preferenze dei cookie
  };
  
  
  // Pubblica un evento su window e per le analitycs di Shopify
  static customEvent = (eventName, data, element) => {
    const e = new CustomEvent(eventName, {
      detail: data
    });
    if(element){
      element.dispatchEvent(e);
    } else {
      window.dispatchEvent(e);
    }
    if(typeof data === 'object' && data != null)
      Shopify.analytics.publish(eventName, data);
  }


  // Aggiunge uno o più prodotti al carrello e pubblica l'evento CART_ADDED. Tutti i parametri sono facoltativi ma ne va passato almeno uno
  static addToCart = ({
    form,     // Form html da cui prendere i dati
    formData, // Oggetto FormData da inviare
    items,     // Array di oggetti da inviare
    success,     // Array di oggetti da inviare
    error     // Array di oggetti da inviare
  }) => {
    if(form) {
      formData = new FormData(form);
    }
    const data = formData || JSON.stringify({items});
    Utils.call({
      request: {
        url: `${Shopify.routes.root}cart/add.js`,
        method: 'post',        
        data: data,
        headers: {
          'content-type': items ? 'application/json' : 'application/x-www-form-urlencoded',
        }
      },
      success: (response) => {
        ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_UPDATED, response);
        ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_ADDED, response);
        success ? success(response) : null;
      },
      error: response => error ? error(response) : null
    });
  }

  // Modifica una linea del carrello e pubblica l'evento CART_UPDATED. Se un prodotto è stato aumentato di quantità pubblica anche CART_ADDED, se invece è stato diminuito o rimosso pubblica CART_REMOVED
  static updateCartLine = (data) => {
    Utils.call({
      request: {
        url: `${Shopify.routes.root}cart/change.js`,
        method: 'post',        
        data: JSON.stringify(data),
        headers: {
          'content-type': 'application/json'
        }
      },
      success: (response) => {
        //in generale emetto l'evento di aggiornamento del carrello
        ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_UPDATED, response);
        if(response.items_removed.length > 0){ //se ci sono elementi rimossi
          ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_REMOVED, response);
        }
        if(response.items_added.length > 0){ //se ci sono elementi aggiunti
          ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_ADDED, response);
        }
      }
    });
  }

  // Modifica il carrello, utile per note e attributi. Pubblica l'evento CART_UPDATED. Se un prodotto è stato aumentato di quantità pubblica anche CART_ADDED, se invece è stato diminuito o rimosso pubblica CART_REMOVED. Solo uno dei parametri è necessario. 
  static updateCart = ({form, formData, data}) => {
    Utils.call({
      request: {
        url: `${Shopify.routes.root}cart/update.js`,
        method: 'post',        
        data: formData || JSON.stringify(data) || new FormData(form),
        headers: {
          'content-type': data ? 'application/json' : 'application/x-www-form-urlencoded',
        }
      },
      success: (response) => {
        ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_UPDATED, response);
        if(response.items_changelog.removed?.length > 0){ //se ci sono elementi rimossi
          ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_REMOVED, response);
        }
        if(response.items_changelog.added?.length > 0){ //se ci sono elementi aggiunti
          ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_ADDED, response);
        }
      }
    });
  }

  // Ottiene il carrello ed esegue il callback al success
  static getCart = (callback) => {
    Utils.call({
      request: {
        url: `${Shopify.routes.root}cart.js`,
        method: 'get'
      },
      success: callback
    });
  }

  // Svuota il carrello ed esegue il callback. Pubblica l'evento CART_UPDATED
  static clearCart = (callback) => {
    Utils.call({
      request: {
        url: `${Shopify.routes.root}cart/clear.js`,
        method: 'post'
      },
      success: (cart) => {
        ShopifyTheme.customEvent(ShopifyTheme.EVENTS.CART_UPDATED, cart);
        if(callback){
          callback(cart);
        }
      }
    });
  }
  
  // Ottiene i dati di un prodotto tramite il suo handle ed esegue callback
  static getProduct = ({handle, callback}) => {
    Utils.call({
      request: {
        url: `${Shopify.routes.root}products/${handle}.js`,
        method: 'get'
      },
      success: callback
    });
  }

  // Ottiene i prodotti raccomandati per un prodotto in base ai settaggi dell'app search and discovery ed esegue il callback
  static getRecommendations = ({product_id, limit=10, intent='related', callback}) => {
    Utils.call({
      request: {
        url: `${Shopify.routes.root}recommendations/products.json?product_id=${product_id}&limit=${limit}&intent=${intent}`,
        method: 'get',
        headers: {
          'content-type': 'application/json'
        }
      },
      success: callback
    });
  }

  // Ottiene l'html della sezione data con i prodotti correlati
  static sectionRecommendations = ({product_id, section_id, limit=50, intent='related', callback}) => {
    Utils.call({
      request: {
        url: `${Shopify.routes.root}recommendations/products?product_id=${product_id}&section_id=${section_id}&limit=${limit}&intent=${intent}`,
        method: 'get',
        headers: {
          'content-type': 'application/json'
        }
      },
      success: callback
    });
  }

  // Avvia una ricerca predittiva ed esegue il callback
  static search = ({q, type, limit, limit_scope, unavailable_products, fields, callback}) => {
    const params = [`q=${q}`];
    if(type) params.push(`resources[type]=${type}`);
    if(limit) params.push(`resources[limit]=${limit}`);
    if(limit_scope) params.push(`resources[limit_scope]=${limit_scope}`);
    if(unavailable_products) params.push(`resources[options][unavailable_products]=${unavailable_products}`);
    if(fields) params.push(`resources[options][fields]=${fields}`);

    Utils.call({
      request: {
        url: `${Shopify.routes.root}search/suggest.json?${params.join('&')}`,
        method: 'get',
        headers: {
          'content-type': 'application/json'
        }
      },
      success: (results) => {
        ShopifyTheme.customEvent(ShopifyTheme.EVENTS.SEARCH_PREDICTIVE, results);
        if(callback){
          callback(results);
        }
      }
    });
  }

  // Ottiene l'html della sezione richiesta con i risultati della ricerca predittiva
  static sectionSearch = ({q, section_id, type, limit, limit_scope, unavailable_products, fields, callback}) => {
    const params = [`q=${q}`, `section_id=${section_id}`];
    if(type) params.push(`resources[type]=${type}`);
    if(limit) params.push(`resources[limit]=${limit}`);
    if(limit_scope) params.push(`resources[limit_scope]=${limit_scope}`);
    if(unavailable_products) params.push(`resources[options][unavailable_products]=${unavailable_products}`);
    if(fields) params.push(`resources[options][fields]=${fields}`);

    Utils.call({
      request: {
        url: `${Shopify.routes.root}search/suggest?${params.join('&')}`,
        method: 'get',
        headers: {
          'content-type': 'application/json'
        }
      },
      success: (results) => {
        ShopifyTheme.customEvent(ShopifyTheme.EVENTS.SEARCH, results);
        if(callback){
          callback(results);
        }
      }
    });
  }
}
window.ShopifyTheme = ShopifyTheme;
export default ShopifyTheme;